import { useEffect, useState, useCallback } from 'react'
import { slowConnectionDetector } from '@/lib/slowConnectionDetector'
import { useOfflineModeStore } from '@/store/useOfflineModeStore'
import { useSlowConnectionBannerStore } from '@/store/useSlowConnectionBannerStore'

type SlowConnectionState = {
  isSlowDetected: boolean
  isDismissed: boolean
  dismiss: () => void
}

/**
 * Subscribes to slow/fast connection transitions from the singleton detector.
 * Returns state for rendering the slow-connection banner.
 * Also syncs banner visibility to a Zustand store so RootLayout can read it
 * for padding without calling this hook a second time.
 *
 * - When offline mode is enabled, detection resets (banner not needed).
 * - When user dismisses, `isDismissed` stays true until a fast->slow re-transition.
 */
export function useSlowConnectionDetector(): SlowConnectionState {
  const [isSlowDetected, setIsSlowDetected] = useState(
    () => slowConnectionDetector.isSlow(),
  )
  const [isDismissed, setIsDismissed] = useState(false)
  const isOfflineModeEnabled = useOfflineModeStore(s => s.isOfflineModeEnabled)
  const setVisible = useSlowConnectionBannerStore(s => s.setVisible)

  useEffect(() => {
    const unsubSlow = slowConnectionDetector.onSlowDetected(() => {
      setIsSlowDetected(true)
      setIsDismissed(false)
    })

    const unsubFast = slowConnectionDetector.onFastDetected(() => {
      setIsSlowDetected(false)
      setIsDismissed(false)
    })

    return () => {
      unsubSlow()
      unsubFast()
    }
  }, [])

  // Reset both React state AND the singleton detector when offline mode is toggled on.
  // Without resetting the detector, its internal `slow=true` would cause the banner
  // to silently NOT reappear if the user later disables offline mode (since the
  // detector wouldn't fire a new onSlowDetected — it already considers itself slow).
  useEffect(() => {
    if (isOfflineModeEnabled) {
      slowConnectionDetector.reset()
      setIsSlowDetected(false)
      setIsDismissed(false)
    }
  }, [isOfflineModeEnabled])

  // Sync derived visibility to the store (consumed by RootLayout for padding)
  const isVisible = isSlowDetected && !isDismissed && !isOfflineModeEnabled
  useEffect(() => {
    setVisible(isVisible)
  }, [isVisible, setVisible])

  const dismiss = useCallback(() => {
    setIsDismissed(true)
  }, [])

  return { isSlowDetected, isDismissed, dismiss }
}
